// New Client Offer enum values, mirrored 1:1 from the Prisma enums in packages/db/prisma/schema.prisma.
// Shared by the API, the admin web app and the public apps without importing @repo/db.

export const NEW_CLIENT_OFFER_STATUSES = ['DRAFT', 'LIVE', 'PAUSED'] as const;
export type NewClientOfferStatus = (typeof NEW_CLIENT_OFFER_STATUSES)[number];

export const OFFER_DISCOUNT_TYPES = ['PERCENT', 'FIXED_AMOUNT'] as const;
export type OfferDiscountType = (typeof OFFER_DISCOUNT_TYPES)[number];

/**
 * Where the sign-up form was shown. STAFF = issued from the staff app (walk-in / phone call); KIOSK = issued
 * at self check-in (kiosk / QR) to a brand-new walk-in, when the salon turns that on; REFERRAL = signed up from a
 * client's Refer a Friend link (/f/{shareCode}); COUNTER_QR = joined at the salon's counter QR (check-in OFF) and
 * used the offer on the spot; CREATOR = signed up from a beauty creator's link (/c/{code}); WEBSITE_EMBED = signed
 * up from the offer form embedded on the salon's own website (/embed/offer); GIVEAWAY = a giveaway thank-you gift
 * texted to an entrant who never came in (no form on this claim); PHONE_CALL = retired, nothing issues it any more and
 * it stays because the database enum keeps the value. A desk tag (walk-in says the code) uses STAFF with a STAFF creator
 * credit, never CREATOR.
 */
export const OFFER_PLACEMENTS = [
  'CAMPAIGN_LANDING',
  'BIO_PAGE_SHEET',
  'BIO_PAGE_BANNER',
  'BOOKING_BANNER',
  'STAFF',
  'KIOSK',
  'REFERRAL',
  'COUNTER_QR',
  'CREATOR',
  'WEBSITE_EMBED',
  'GIVEAWAY',
  'PHONE_CALL',
] as const;
export type OfferPlacement = (typeof OFFER_PLACEMENTS)[number];

/** Placements a guest can sign up from on a public page (STAFF / KIOSK / COUNTER_QR are issued in the salon). */
export const PUBLIC_OFFER_PLACEMENTS = [
  'CAMPAIGN_LANDING',
  'BIO_PAGE_SHEET',
  'BIO_PAGE_BANNER',
  'BOOKING_BANNER',
  'REFERRAL',
  'CREATOR',
  'WEBSITE_EMBED',
] as const satisfies readonly OfferPlacement[];
export type PublicOfferPlacement = (typeof PUBLIC_OFFER_PLACEMENTS)[number];
/** The offer form on the Bio Page (its sheet or its banner): the guest's place reads "Bio Page". */
export const BIO_OFFER_PLACEMENTS = ['BIO_PAGE_SHEET', 'BIO_PAGE_BANNER'] as const satisfies readonly OfferPlacement[];
/** A sign-up of the offer form on the Bio Page (a plain campaign sign-up has no `newClientOfferId`). */
export const isBioOfferSignUp = (signUp: { newClientOfferId: string | null; placement: OfferPlacement }): boolean =>
  signUp.newClientOfferId !== null && (BIO_OFFER_PLACEMENTS as readonly OfferPlacement[]).includes(signUp.placement);
/**
 * Forms that are their own arrival, off the Bio Page: a creator's page and the embed on the salon's own website.
 * A Bio Page visit key still open in the browser never takes the credit — the sign-up is read from its own
 * referrer, so Bio Page analytics never claims a sign-up made somewhere else.
 */
export const OWN_ARRIVAL_OFFER_PLACEMENTS = ['CREATOR', 'WEBSITE_EMBED'] as const satisfies readonly OfferPlacement[];
export const isOwnArrivalOfferPlacement = (placement: OfferPlacement): boolean =>
  (OWN_ARRIVAL_OFFER_PLACEMENTS as readonly OfferPlacement[]).includes(placement);
/**
 * Placements whose guest is a lead Hayven brought in: every public sign-up form and a giveaway entrant texted the
 * thank-you gift (GIVEAWAY). GIVEAWAY is not a public form placement: no public sign-up may post it.
 */
export const LEAD_OFFER_PLACEMENTS = [
  ...PUBLIC_OFFER_PLACEMENTS,
  'GIVEAWAY',
] as const satisfies readonly OfferPlacement[];
/** Joined in the salon (STAFF / KIOSK / COUNTER_QR): no form was filled in, so the guest is no lead. Retired PHONE_CALL reads the same. */
export const isInSalonOfferPlacement = (placement: OfferPlacement): boolean =>
  !(LEAD_OFFER_PLACEMENTS as readonly OfferPlacement[]).includes(placement);

/**
 * Claims the salon issues on the spot to a walk-in who never signed up (self check-in, counter QR): good until the
 * salon closes today, no pass text, nothing for Meta to match. Such a claim that ran out unused is no claim at all
 * (offered again next time), and it becomes a full pass once the guest really signs up on a page.
 */
export const TODAY_ONLY_OFFER_PLACEMENTS = ['KIOSK', 'COUNTER_QR'] as const satisfies readonly OfferPlacement[];
export const isTodayOnlyOfferPlacement = (placement: OfferPlacement): boolean =>
  (TODAY_ONLY_OFFER_PLACEMENTS as readonly OfferPlacement[]).includes(placement);

export const OFFER_SIGNUP_CHANNELS = [
  'INSTAGRAM',
  'FACEBOOK',
  'GOOGLE',
  'TIKTOK',
  'WEBSITE',
  'DIRECT',
  'OTHER',
] as const;
export type OfferSignupChannel = (typeof OFFER_SIGNUP_CHANNELS)[number];

/** Claim state machine: ACTIVE → BOOKED → REDEEMED, with NO_SHOW / EXPIRED / NOT_ELIGIBLE side states. */
export const OFFER_CLAIM_STATUSES = [
  'ACTIVE',
  'BOOKED',
  'NO_SHOW',
  'REDEEMED',
  'EXPIRED',
  'NOT_ELIGIBLE',
] as const;
export type OfferClaimStatus = (typeof OFFER_CLAIM_STATUSES)[number];

export const OFFER_NOT_ELIGIBLE_REASONS = [
  'EXISTING_CLIENT',
  'IMPORTED_CLIENT',
  'ALREADY_REDEEMED',
  'STAFF_MARKED',
] as const;
export type OfferNotEligibleReason = (typeof OFFER_NOT_ELIGIBLE_REASONS)[number];

/**
 * `CHECK_IN` is history only: offers redeemed at check-in before the offer was used at visit Done. `COUNTER_QR`:
 * the guest used it themselves at the counter QR of a salon with check-in OFF (no visit).
 */
export const OFFER_REDEMPTION_METHODS = ['AUTO_VISIT_COMPLETED', 'STAFF_LOOKUP', 'CHECK_IN', 'COUNTER_QR'] as const;
export type OfferRedemptionMethod = (typeof OFFER_REDEMPTION_METHODS)[number];

/**
 * How a salon's guests use the offer — derived from the salon's check-in switch, never stored:
 * `CHECK_IN` = check-in holds it and the visit Done uses it; `COUNTER_QR` = check-in is off, so the guest scans
 * the counter QR and uses it themselves on the spot.
 */
export const OFFER_REDEEM_MODES = ['CHECK_IN', 'COUNTER_QR'] as const;
export type OfferRedeemMode = (typeof OFFER_REDEEM_MODES)[number];

/**
 * How a guest uses any gift at the salon (the card page's rule, shared by the prize card and Refer a Friend rewards):
 * tap the Hayven Tap stand · check in with the phone number · scan the printed counter sign · the front desk takes it
 * off at a Hayven POS checkout.
 */
export const GIFT_HOW_TO_USE = ['TAP', 'CHECK_IN', 'COUNTER_SIGN', 'AT_CHECKOUT'] as const;
export type GiftHowToUse = (typeof GIFT_HOW_TO_USE)[number];

export const OFFER_CLAIM_EVENT_TYPES = [
  'SIGNED_UP',
  'SIGNED_UP_AGAIN',
  'MESSAGE_SENT',
  'BOOKED',
  'BOOKING_RELEASED',
  'NO_SHOW',
  'REDEEMED',
  'REDEEM_UNDONE',
  'EXPIRED',
  'MARKED_NOT_ELIGIBLE',
  'CHECKED_IN',
  'CREATOR_SET',
] as const;
export type OfferClaimEventType = (typeof OFFER_CLAIM_EVENT_TYPES)[number];

/** The offer's SMS journey. No post-visit / review step: the salon-wide review request owns that. */
export const OFFER_MESSAGE_STEPS = [
  'PASS',
  'REMINDER_1',
  'REMINDER_2',
  'LAST_CHANCE',
  'NO_SHOW',
  'WELCOME_BACK',
] as const;
export type OfferMessageStep = (typeof OFFER_MESSAGE_STEPS)[number];
